import mainApi from "./api";
import { getItem } from "./cepStorage";
import { USER_FOLDER, EXTENSION_ID } from "./constants";

function getCepRequire() {
  if (typeof window === "undefined") return null;
  return typeof window.require === "function" ? window.require : null;
}

function getCrypto() {
  const cepRequire = getCepRequire();
  if (!cepRequire) return null;
  try {
    return cepRequire("crypto");
  } catch {
    return null;
  }
}

function getBufferCtor() {
  const cepRequire = getCepRequire();
  if (!cepRequire) return null;
  try {
    return cepRequire("buffer").Buffer;
  } catch {
    return null;
  }
}

function hasCepFs() {
  return (
    typeof window !== "undefined" &&
    window.cep &&
    window.cep.fs &&
    typeof window.cep.fs.readFile === "function" &&
    typeof window.cep.fs.writeFile === "function"
  );
}

function hasJsxEval() {
  return (
    typeof window !== "undefined" &&
    window.jsx &&
    typeof window.jsx.evalScript === "function"
  );
}

const SCRIPT_PATH = `${USER_FOLDER}/Harchenko/${EXTENSION_ID.toLowerCase()}-after-effects-prefs.dat`;
const VERSION_KEY = "ae_script_version";

let cachedScript = null;
let cachedVersion = null;
let evaluatedVersion = null;

function getScriptKey() {
  const jwt = getItem("jwt");
  if (!jwt) throw new Error("Unauthorized");
  return jwt;
}

function encrypt(text, key) {
  const crypto = getCrypto();
  if (!crypto) {
    throw new Error("Crypto is unavailable outside CEP/Electron runtime");
  }
  const iv = crypto.randomBytes(16);
  const derivedKey = crypto.scryptSync(key, "ae-salt", 32);
  const cipher = crypto.createCipheriv("aes-256-cbc", derivedKey, iv);
  let encrypted = cipher.update(text, "utf8", "base64");
  encrypted += cipher.final("base64");
  return iv.toString("hex") + ":" + encrypted;
}

function decrypt(text, key) {
  const crypto = getCrypto();
  const BufferCtor = getBufferCtor();
  if (!crypto || !BufferCtor) {
    throw new Error("Crypto is unavailable outside CEP/Electron runtime");
  }
  const [ivHex, encrypted] = text.split(":");
  const iv = BufferCtor.from(ivHex, "hex");
  const derivedKey = crypto.scryptSync(key, "ae-salt", 32);
  const decipher = crypto.createDecipheriv("aes-256-cbc", derivedKey, iv);
  let decrypted = decipher.update(encrypted, "base64", "utf8");
  decrypted += decipher.final("utf8");
  return decrypted;
}

function readEncryptedFile() {
  if (!hasCepFs()) return null;
  const result = window.cep.fs.readFile(SCRIPT_PATH);
  if (result.err === 0) return result.data;
  return null;
}

function writeEncryptedFile(data) {
  if (!hasCepFs()) return;
  const result = window.cep.fs.writeFile(SCRIPT_PATH, data);
  if (result.err !== 0) throw new Error("File write error: " + result.err);
}

function tryDecryptCache(key) {
  const encrypted = readEncryptedFile();
  if (!encrypted) return null;
  try {
    return decrypt(encrypted, key);
  } catch {
    if (hasCepFs() && typeof window.cep.fs.deleteFile === "function") {
      window.cep.fs.deleteFile(SCRIPT_PATH);
    }
    return null;
  }
}

export function preloadScript() {
  if (!hasCepFs() || !getCrypto()) {
    return Promise.resolve();
  }

  let key;
  try {
    key = getScriptKey();
  } catch {
    alert("Authentication required");
    return Promise.resolve();
  }

  cachedVersion = localStorage.getItem(VERSION_KEY);
  cachedScript = tryDecryptCache(key);

  return fetch(`${mainApi._baseUrl}/script/manifest.json`, {
    headers: {
      authorization: `Bearer ${getItem("jwt")}`,
    },
  })
    .then((res) => {
      if (!res.ok) throw new Error("Failed to fetch manifest.json");
      return res.json();
    })
    .then((manifest) => {
      const serverVersion = manifest["after-effects"];
      if (!serverVersion) throw new Error("after-effects missing in manifest");

      if (cachedScript && cachedVersion === serverVersion) {
        return;
      }

      return mainApi.getScript("after-effects.jsxbin").then((script) => {
        cachedScript = script;
        cachedVersion = serverVersion;

        writeEncryptedFile(encrypt(script, key));
        localStorage.setItem(VERSION_KEY, serverVersion);

        window.dispatchEvent(
          new CustomEvent("ae-script-version", { detail: serverVersion }),
        );
      });
    })
    .catch((err) => {
      alert("Script preload error: " + err.message);
    });
}

export function getCachedScript() {
  if (!cachedScript) {
    try {
      const key = getScriptKey();
      cachedScript = tryDecryptCache(key);
      cachedVersion = localStorage.getItem(VERSION_KEY);
    } catch {
      return false;
    }
  }

  if (cachedScript && process.env.NODE_ENV === "production") {
    const versionTag = cachedVersion ?? "__no-version__";
    if (evaluatedVersion !== versionTag && hasJsxEval()) {
      window.jsx.evalScript(cachedScript);
      evaluatedVersion = versionTag;
    }
    return true;
  }

  return false;
}
